import { addHours } from 'date-fns'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { StorageKeys } from '~/atoms/StorageKeys'
import { ClockSkewBar } from '~/components/shared/ClockSkewBar'
import { useClockSkewCheck } from '~/hooks/useClockSkewCheck'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'

// How long to stay quiet after a manual dismissal before nudging again. Clock
// skew is persistent, so we re-surface within the same workday rather than
// once-and-done.
const DISMISS_SNOOZE_HOURS = 8

interface WrapperProps {
  $show: boolean
}

const Wrapper = styled.div<WrapperProps>`
  height: ${({ $show }) => ($show ? '56' : '0')}px;
  overflow: hidden;
  transition: height 200ms ease;
`

interface ClockSkewBannerState {
  lastDismissedDate?: Date | undefined
}

const clockSkewBannerStateAtom = atomWithStorage<ClockSkewBannerState>(
  StorageKeys.ClockSkewBanner,
  {},
)

const ClockSkewBanner = ({ onShowCallback }: BannerProps) => {
  const [show, setShow] = useState(false)
  const { isClockSkewed } = useClockSkewCheck()
  const [bannerState, setBannerState] = useAtom(clockSkewBannerStateAtom)

  const {
    state: {
      phoneCalls: {
        list: { callList },
      },
    },
  } = usePhoneEndpoint({})

  const shouldShowBanner = useCallback((): boolean => {
    if (!isClockSkewed) return false
    if (!bannerState.lastDismissedDate) return true
    return (
      addHours(new Date(bannerState.lastDismissedDate), DISMISS_SNOOZE_HOURS) <
      new Date()
    )
  }, [isClockSkewed, bannerState.lastDismissedDate])

  const handleSetShow = useCallback(
    (val: boolean) => {
      setShow(val)
      if (!val) {
        setBannerState({ lastDismissedDate: new Date() })
      }
      onShowCallback(val)
    },
    [setBannerState, onShowCallback],
  )

  // Don't cover the call UI while the user is on a call.
  const tempHideBanner = useMemo(() => callList.size > 0, [callList])

  // Gate on live isClockSkewed too: the poll flips it back to false once the
  // user fixes their clock, but the show-effect early-returns while `show` is
  // true — so without this the banner would linger until manual dismissal.
  const willShowBanner = isClockSkewed && !tempHideBanner && show

  useEffect(() => {
    if (show) return

    if (shouldShowBanner()) {
      // Defer the state update to the next tick, matching the sibling banners.
      const id = setTimeout(() => handleSetShow(true), 0)
      return () => clearTimeout(id)
    }

    // Snoozed: nothing in this effect's deps changes as the snooze window
    // elapses, so schedule an explicit re-surface at expiry — otherwise a
    // long-lived (desktop) session stays hidden until reload.
    if (isClockSkewed && bannerState.lastDismissedDate) {
      const msUntilResurface =
        addHours(
          new Date(bannerState.lastDismissedDate),
          DISMISS_SNOOZE_HOURS,
        ).getTime() - new Date().getTime()
      if (msUntilResurface > 0) {
        const id = setTimeout(() => handleSetShow(true), msUntilResurface)
        return () => clearTimeout(id)
      }
    }
  }, [
    show,
    shouldShowBanner,
    handleSetShow,
    isClockSkewed,
    bannerState.lastDismissedDate,
  ])

  useEffect(() => {
    if (!show) return
    onShowCallback(willShowBanner)
  }, [willShowBanner, show, onShowCallback])

  const dismissBanner = useCallback(() => handleSetShow(false), [handleSetShow])

  return (
    <Wrapper $show={willShowBanner}>
      <ClockSkewBar onDismiss={dismissBanner} />
    </Wrapper>
  )
}

export default ClockSkewBanner
